import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { ScrollArea } from '../ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Tag, Loader2 } from 'lucide-react';

const getColorOptions = (t) => [
  { value: '#E63946', label: t('types.colors.red') },
  { value: '#1D3557', label: t('types.colors.darkBlue') },
  { value: '#457B9D', label: t('types.colors.blue') },
  { value: '#2A9D8F', label: t('types.colors.teal') },
  { value: '#E9C46A', label: t('types.colors.yellow') },
  { value: '#F4A261', label: t('types.colors.orange') },
  { value: '#9B2335', label: t('types.colors.maroon') },
  { value: '#6B7280', label: t('types.colors.gray') },
];

export const SpecialistTypeFormPanel = ({ 
  specialistType, 
  isOpen, 
  onClose, 
  onSubmit 
}) => {
  const { t } = useTranslation('specialists');
  const colorOptions = getColorOptions(t);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    color: '#E63946',
    requires_license: true,
    default_hourly_rate: '',
    notes: ''
  });

  useEffect(() => {
    if (isOpen) {
      if (specialistType) {
        setFormData({
          name: specialistType.name || '',
          code: specialistType.code || '',
          description: specialistType.description || '',
          color: specialistType.color || '#E63946',
          requires_license: specialistType.requires_license ?? true,
          default_hourly_rate: specialistType.default_hourly_rate || '',
          notes: specialistType.notes || ''
        });
      } else {
        setFormData({
          name: '',
          code: '',
          description: '',
          color: '#E63946',
          requires_license: true,
          default_hourly_rate: '',
          notes: ''
        });
      }
    }
  }, [specialistType, isOpen]);

  const handleSubmit = async () => {
    if (!formData.name || !formData.code) return;
    
    setLoading(true);
    const submitData = {
      ...formData,
      default_hourly_rate: formData.default_hourly_rate ? parseFloat(formData.default_hourly_rate) : null
    };
    try {
      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error('Error saving specialist type:', error);
    }
    setLoading(false);
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-md overflow-hidden flex flex-col p-0">
        <SheetHeader className="px-6 py-4 border-b bg-gradient-to-r from-[#E63946]/5 to-transparent">
          <div className="flex items-center gap-3">
            <div 
              className="h-12 w-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${formData.color}20` }}
            >
              <Tag className="h-6 w-6" style={{ color: formData.color }} />
            </div>
            <div>
              <SheetTitle className="text-lg font-semibold">
                {specialistType?.id ? t('types.form.editTitle') : t('types.form.title')}
              </SheetTitle>
              <SheetDescription>
                {specialistType?.id ? t('types.form.editDescription') : t('types.form.description')}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1 px-6 py-4">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('types.form.name')} *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder={t('types.form.namePlaceholder')}
                  data-testid="type-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('types.form.code')} *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  placeholder={t('types.form.codePlaceholder')}
                  data-testid="type-code-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('types.form.description')}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder={t('types.form.descriptionPlaceholder')}
                rows={2}
                data-testid="type-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('types.form.color')}</Label>
                <Select
                  value={formData.color}
                  onValueChange={(value) => setFormData({...formData, color: value})}
                >
                  <SelectTrigger data-testid="type-color-select">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-4 h-4 rounded-full" 
                        style={{ backgroundColor: formData.color }}
                      />
                      <SelectValue />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {colorOptions.map((color) => (
                      <SelectItem key={color.value} value={color.value}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-4 h-4 rounded-full" 
                            style={{ backgroundColor: color.value }}
                          />
                          {color.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('types.form.hourlyRate')}</Label>
                <Input
                  type="number"
                  value={formData.default_hourly_rate}
                  onChange={(e) => setFormData({...formData, default_hourly_rate: e.target.value})}
                  placeholder={t('types.form.hourlyRatePlaceholder')}
                  data-testid="type-rate-input"
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border">
              <div>
                <Label>{t('types.form.requiresLicense')}</Label>
                <p className="text-sm text-muted-foreground">{t('types.form.requiresLicenseDescription')}</p>
              </div>
              <Switch
                checked={formData.requires_license}
                onCheckedChange={(checked) => setFormData({...formData, requires_license: checked})}
                data-testid="type-license-switch"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('types.form.notes')}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder={t('types.form.notesPlaceholder')}
                rows={2}
              />
            </div>
          </div>
        </ScrollArea>

        <div className="px-6 py-4 border-t bg-muted/30 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>{t('common:cancel')}</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || !formData.name || !formData.code} 
            className="bg-[#E63946] hover:bg-[#d32836]"
            data-testid="save-type-btn"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {specialistType?.id ? t('types.form.update') : t('types.form.create')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default SpecialistTypeFormPanel;
